import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SOCIAL_LABELS, type SocialConnectionSettings, type SocialProvider } from '@kontor/shared';
import { api, ApiError } from '../../api';

const errorText = (e: unknown, fallback: string): string => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const RETURN_ERRORS: Record<string, string> = {
  ungueltig: 'Die Anmeldung war nicht gültig. Bitte versuchen Sie es noch einmal.',
  abgelaufen: 'Die Anmeldung hat zu lange gedauert. Bitte versuchen Sie es noch einmal.',
  abgebrochen: 'Sie haben den Zugriff nicht erteilt. Es wurde nichts verbunden.',
  dienst: 'Der Dienst hat die Anmeldung abgelehnt. Prüfen Sie, ob Sie auf mindestens einer Seite Administrator sind, und versuchen Sie es noch einmal.',
};

const ACCOUNT_LABEL: Record<SocialProvider, string> = { facebook: 'Facebook-Seite', linkedin: 'LinkedIn-Unternehmensseite', tiktok: 'Konto' };

/**
 * Facebook, LinkedIn und TikTok verbinden (KON-190 bis KON-192). Der Mandant meldet sich beim Dienst an und erteilt Wirkia den Zugriff auf seine Seite, die Zugangsdaten
 * landen verschlüsselt auf dem Server und kommen nie in die Oberfläche. Mehrere Seiten: Der Mandant wählt, über welche Wirkia arbeitet.
 */
export function SocialIntegration({ provider }: { provider: SocialProvider }) {
  const client = useQueryClient();
  const [params] = useSearchParams();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [manual, setManual] = useState('');
  const current = useQuery({ queryKey: ['integrations', 'social', provider], queryFn: async () => (await api<{ data: SocialConnectionSettings | null }>('GET', `/integrations/social/${provider}`)).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['integrations'] });
  const returned = params.get('verbunden') ? { ok: true, text: `Verbunden. Wählen Sie unten, über welche ${ACCOUNT_LABEL[provider]} Wirkia arbeiten soll.` } : params.get('fehler') ? { ok: false, text: RETURN_ERRORS[params.get('fehler')!] ?? 'Die Verbindung hat nicht geklappt.' } : null;

  const connect = useMutation({
    mutationFn: async () => (await api<{ data: { url: string } }>('POST', `/integrations/social/${provider}/connect`)).data.url,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Verbindung konnte nicht gestartet werden.') }),
  });
  const choose = useMutation({
    mutationFn: async (accountId: string) => (await api<{ data: SocialConnectionSettings }>('PUT', `/integrations/social/${provider}/account`, { accountId })).data,
    onSuccess: (s) => {
      refresh();
      setMessage({ ok: true, text: `Wirkia arbeitet jetzt über „${s.label}“.` });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Auswahl hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/integrations/${provider}`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Die Verbindung ist getrennt, die gespeicherten Zugangsdaten sind gelöscht.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Trennen hat nicht geklappt.') }),
  });

  if (current.isLoading) return <p className="muted">Wird geladen …</p>;
  const s = current.data;
  const shown = message ?? returned;
  const expired = s?.expiresAt ? Date.parse(s.expiresAt) < Date.now() : false;

  return (
    <>
      {s?.status === 'error' && s.lastError && <div className="alert alert--error" role="alert">{s.lastError}</div>}
      {shown && <div className={`alert ${shown.ok ? 'alert--ok' : 'alert--error'}`} role={shown.ok ? 'status' : 'alert'}>{shown.text}</div>}

      <div className="card">
        <h2>{s ? 'Verbunden' : 'Verbinden'}</h2>
        {s ? (
          <p className="small">Wirkia arbeitet über <strong>{s.label}</strong>.{s.expiresAt && !expired ? ` Die Anmeldung gilt bis ${dayFormat.format(new Date(s.expiresAt))}, danach verbinden Sie neu.` : ''}</p>
        ) : (
          <p className="small">Sie melden sich bei {SOCIAL_LABELS[provider]} an und erlauben Wirkia den Zugriff auf Ihre Seite. Wirkia sieht dabei nie Ihr Passwort.</p>
        )}
        <div className="form-actions">
          <button className="btn btn--primary" type="button" disabled={connect.isPending} onClick={() => { setMessage(null); connect.mutate(); }}>
            {connect.isPending ? 'Einen Augenblick …' : s ? `Neu mit ${SOCIAL_LABELS[provider]} verbinden` : `Mit ${SOCIAL_LABELS[provider]} verbinden`}
          </button>
          {s && <button className="btn btn--secondary" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm('Verbindung trennen? Wirkia kann danach nicht mehr in Ihrem Namen veröffentlichen.')) remove.mutate(); }}>Trennen</button>}
        </div>
      </div>

      {s && provider !== 'tiktok' && (
        <div className="card">
          <h2>{ACCOUNT_LABEL[provider]}</h2>
          {s.accounts.length > 0 ? (
            <label className="field">
              <span>Wirkia arbeitet über</span>
              <select className="input" value={s.selectedId ?? ''} disabled={choose.isPending} onChange={(e) => { setMessage(null); choose.mutate(e.target.value); }}>
                {s.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          ) : (
            <>
              <p className="small muted">LinkedIn hat keine Liste Ihrer Seiten geliefert. Tragen Sie die Kennung Ihrer Unternehmensseite ein: die Zahl in der Adresse Ihrer Seite (linkedin.com/company/<strong>12345678</strong>/admin).</p>
              <div className="form-grid">
                <label className="field"><span>Kennung der Seite</span><input className="input" inputMode="numeric" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={s.selectedId ?? '12345678'} /></label>
              </div>
              <div className="form-actions">
                <button className="btn btn--secondary" type="button" disabled={choose.isPending || !manual.trim()} onClick={() => { setMessage(null); choose.mutate(manual.trim()); }}>Übernehmen</button>
              </div>
            </>
          )}
        </div>
      )}

      {s && provider === 'facebook' && (
        <div className="card">
          <h2>Was Wirkia mit Facebook macht</h2>
          <ul className="small">
            <li>Beiträge veröffentlichen: im Modul Marketing unter „Beiträge“.</li>
            <li>Anfragen empfangen: Wer ein Lead-Formular Ihrer Seite ausfüllt, erscheint als Anfrage mit dem Typ „facebook-lead“.</li>
            <li>Nachrichten beantworten: Nachrichten an Ihre Seite erscheinen unter „Live Chats“. Facebook erlaubt Antworten nur innerhalb von 24 Stunden nach der letzten Nachricht des Kunden.</li>
          </ul>
        </div>
      )}
      {s && provider === 'tiktok' && (
        <div className="card">
          <h2>Was Wirkia mit TikTok macht</h2>
          <p className="small">Wirkia veröffentlicht Videos von einer Internetadresse, im Modul Marketing unter „Beiträge“. Solange TikTok die Wirkia-App nicht geprüft hat, erscheinen die Videos nur für Sie selbst (privat). TikTok bietet keine Schnittstelle für Nachrichten oder Formulare.</p>
        </div>
      )}
    </>
  );
}
